import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GripVertical, X, Plus } from 'lucide-react'
import { ListBox, ListBoxItem, Popover, DialogTrigger } from 'react-aria-components'
import { Button } from '@/components/base/buttons'
import { getLanguageFlag, getLanguageDisplayName, SUPPORTED_LANGUAGES } from '@/lib/languageFlags'
import { cn } from '@/lib/utils'

interface SortableLanguagesInputProps {
  value: string[]
  onChange: (value: string[]) => void
  excludeLanguages?: string[]
  className?: string
}

export function SortableLanguagesInput({
  value = [],
  onChange,
  excludeLanguages = [],
  className,
}: SortableLanguagesInputProps) {
  const { t, i18n } = useTranslation()
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const translatedLanguages = useMemo(() => {
    return SUPPORTED_LANGUAGES.map((lang) => ({
      ...lang,
      name: getLanguageDisplayName(lang.id, i18n.language, lang.name) || lang.name,
    }))
  }, [i18n.language])

  // Get selected languages in order with their data
  const selectedLanguages = useMemo(() => {
    return value
      .map((langCode) => translatedLanguages.find((choice) => choice.id === langCode))
      .filter(Boolean) as typeof SUPPORTED_LANGUAGES
  }, [value, translatedLanguages])

  // Get available choices (not yet selected and not in excludeLanguages)
  const availableChoices = useMemo(() => {
    const allExcluded = [...excludeLanguages, ...value]
    return translatedLanguages.filter((choice) => !allExcluded.includes(choice.id))
  }, [value, excludeLanguages, translatedLanguages])

  const handleAdd = (langId: string) => {
    onChange([...value, langId])
    setIsMenuOpen(false)
  }

  const handleRemove = (langCode: string) => {
    onChange(value.filter((code) => code !== langCode))
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      return
    }

    const newValue = [...value]
    const [removed] = newValue.splice(draggedIndex, 1)
    newValue.splice(dropIndex, 0, removed)

    onChange(newValue)
    setDraggedIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Add language button with dropdown */}
      <DialogTrigger isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <Button
          type="button"
          color="secondary"
          className="self-start"
          isDisabled={availableChoices.length === 0}
          iconLeading={Plus}
        >
          {t('resources.assistants.personality.add_language')}
        </Button>

        <Popover
          placement="bottom start"
          className={cn(
            'z-50 min-w-[240px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg',
            'dark:border-gray-700 dark:bg-gray-800',
            'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
            'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
          )}
        >
        <ListBox
          className="max-h-60 overflow-auto p-1 focus:outline-none"
          items={availableChoices}
          onAction={(key) => handleAdd(key as string)}
          aria-label={t('resources.assistants.personality.available_languages', { defaultValue: 'Available languages' })}
        >
            {(item) => (
              <ListBoxItem
                id={item.id}
                textValue={item.name}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none',
                  'hover:bg-gray-100 dark:hover:bg-gray-700',
                  'focus:bg-gray-100 dark:focus:bg-gray-700'
                )}
              >
                <span className="text-lg leading-none">{getLanguageFlag(item.id)}</span>
                <span className="text-gray-900 dark:text-gray-100">{item.name}</span>
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </DialogTrigger>

      {/* Selected languages - sortable */}
      {selectedLanguages.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.personality.reorder_hint')}
          </p>

          {selectedLanguages.map((lang, index) => (
            <div
              key={lang.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={cn(
                'flex cursor-move items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 transition-all',
                'hover:border-primary-300 hover:bg-gray-50',
                'dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-600 dark:hover:bg-gray-700',
                draggedIndex === index && 'opacity-50'
              )}
            >
              <GripVertical className="h-5 w-5 shrink-0 cursor-grab text-gray-400" />

              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                )}
              >
                {index + 1}
              </span>

              <span className="text-lg leading-none">{getLanguageFlag(lang.id)}</span>

              <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">
                {lang.name}
              </span>

              <button
                type="button"
                onClick={() => handleRemove(lang.id)}
                className={cn(
                  'rounded p-1 text-gray-400 transition-colors',
                  'hover:bg-gray-100 hover:text-gray-600',
                  'dark:hover:bg-gray-700 dark:hover:text-gray-300'
                )}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SortableLanguagesInput
